/**
 * Local calendar `Date` values for the shared DateField / react-day-picker.
 *
 * Parsing still goes through the one helper in `@/shared/dates`. The picker needs a
 * civil year/month/day in the host timezone; we rebuild that from the UTC midnight
 * `parseBusinessDate` returns so the day never shifts.
 */

import {
  berlinToday,
  formatBusinessDate,
  isBusinessDate,
  parseBusinessDate,
  type BusinessDate,
} from "@/shared/dates";

const DEFAULT_FROM_YEAR = 2000;
const DEFAULT_TO_YEAR_OFFSET = 2;

/** Local midnight Date for the picker's civil day. */
export function calendarDateFromBusiness(value: BusinessDate): Date {
  const utc = parseBusinessDate(value);
  return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
}

/** `YYYY-MM-DD` from a picker Date (local Y/M/D). */
export function businessDateFromCalendar(date: Date): BusinessDate {
  return formatBusinessDate(
    new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())),
  );
}

/**
 * Dropdown year window: 2000 … Berlin today + 2 (inclusive).
 * Expands to include min / max / value years so assignment and project bounds stay selectable.
 */
export function dateFieldYearWindow(options: {
  min?: string;
  max?: string;
  value?: string;
  today?: BusinessDate;
}): { fromYear: number; toYear: number } {
  const today = options.today ?? berlinToday();
  let fromYear = DEFAULT_FROM_YEAR;
  let toYear = Number(today.slice(0, 4)) + DEFAULT_TO_YEAR_OFFSET;

  for (const iso of [options.min, options.max, options.value]) {
    if (iso && isBusinessDate(iso)) {
      const year = Number(iso.slice(0, 4));
      fromYear = Math.min(fromYear, year);
      toYear = Math.max(toYear, year);
    }
  }
  return { fromYear, toYear };
}
